import { Injectable } from "@angular/core";

import { ContentConfigService } from "../content/content.config";
import { TranslationService } from "../translation/translation.service";

@Injectable({
	providedIn: "root",
})
export class MessageCodeFallbackService {
	FALLBACK_DELIMITER = "_";
	KEY_DELIMITER = ".";
	ERROR_MESSAGE_PREFIX = "e";
	GENERAL_MESSAGE_PREFIX = "general";

	constructor(
		private translationService: TranslationService,
		private config: ContentConfigService
	) {}

	get(key: string): string {
		return this._get(key);
	}

	getOrThrow(key: string): string {
		return this._get(key, false);
	}

	private _get(key: string, allowNoMatch = true): string {
		let translation = this.translationService.getByKey(key);

		if (translation !== key) {
			return translation;
		}

		translation = this.attemptFetchGeneralMessage(key);

		if (translation !== key) {
			return translation;
		}

		const result = this.recursiveGet(key);

		if (!result && !allowNoMatch) {
			throw Error(`Key ${key} did not match any translation`);
		}

		return result || key;
	}

	private recursiveGet(key: string): string | undefined {
		const split = key.split(this.KEY_DELIMITER);

		if (split.length !== 2) {
			return key;
		}

		const [prefix, suffix] = split;

		if (suffix) {
			const splitSuffix = suffix.split(this.FALLBACK_DELIMITER);

			if (splitSuffix.length > 1) {
				splitSuffix.pop();

				const newSuffix = splitSuffix.join(this.FALLBACK_DELIMITER);
				const newKey = [prefix, newSuffix].join(this.KEY_DELIMITER);
				let translation = this.translationService.getByKey(newKey);

				if (translation !== newKey) {
					return translation;
				}

				translation = this.attemptFetchGeneralMessage(newKey);

				if (translation !== newKey) {
					return translation;
				}

				return this.recursiveGet(newKey);
			}

			// recursive search exhausted, attempting to translate using general error key
			const messageType = splitSuffix.join();

			const generalKey =
				messageType === this.ERROR_MESSAGE_PREFIX
					? this.config.getGeneralErrorKey()
					: this.config.getGeneralMessageKey();

			const generalTranslation = this.translationService.getByKey(generalKey);

			if (generalTranslation === generalKey) {
				return undefined;
			}

			return generalTranslation;
		}

		return key;
	}

	private attemptFetchGeneralMessage(key: string): string {
		const split = key.split(this.KEY_DELIMITER);

		if (split.length !== 2) {
			return key;
		}

		const generalKey = [this.GENERAL_MESSAGE_PREFIX, split[1]].join(this.KEY_DELIMITER);

		const generalTranslation = this.translationService.getByKey(generalKey);

		if (generalTranslation !== generalKey) {
			return generalTranslation;
		}

		return key;
	}
}
